import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import { useEffect, useRef } from 'react';
import type { WmsBasemapSettings } from './types';

const MOSCOW_CENTER = fromLonLat([37.6173, 55.7558]);

type BasemapPreviewMapProps = {
  urlTemplate: string;
  tileSize: 256 | 512;
  minZoom: number;
  maxZoom: number;
  /** Set (with wmsSettings) to preview a WMS GetMap source instead of an
   * XYZ {z}/{x}/{y} template — urlTemplate is then the WMS base URL. */
  wms?: WmsBasemapSettings;
  className?: string;
};

export function BasemapPreviewMap({ urlTemplate, tileSize, minZoom, maxZoom, wms, className = '' }: BasemapPreviewMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const layerRef = useRef<TileLayer<XYZ | OSM | TileWMS> | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const layer = new TileLayer({ source: new OSM() });
    layerRef.current = layer;
    const map = new Map({
      target: containerRef.current,
      view: new View({ center: MOSCOW_CENTER, zoom: 12, minZoom: 2, maxZoom: 20 }),
      layers: [layer],
      controls: [],
      interactions: [],
    });
    mapRef.current = map;
    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!layerRef.current) return;
    if (!urlTemplate.trim()) {
      layerRef.current.setSource(new OSM());
      return;
    }
    if (wms?.layer) {
      layerRef.current.setSource(
        new TileWMS({
          url: urlTemplate,
          params: { LAYERS: wms.layer, VERSION: wms.version || '1.3.0', FORMAT: wms.format || 'image/png', TRANSPARENT: true },
          crossOrigin: 'anonymous',
        }),
      );
      return;
    }
    layerRef.current.setSource(
      new XYZ({
        url: urlTemplate,
        tileSize,
        minZoom,
        maxZoom,
        crossOrigin: 'anonymous',
      }),
    );
  }, [urlTemplate, tileSize, minZoom, maxZoom, wms]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handle = () => map.updateSize();
    window.addEventListener('resize', handle);
    const timeout = setTimeout(handle, 50);
    return () => {
      window.removeEventListener('resize', handle);
      clearTimeout(timeout);
    };
  });

  return <div ref={containerRef} className={className} />;
}
